import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import useCustomizableCloset from "@hooks/use-customizable-closet";
import useFeatures from "@hooks/use-features";
import {
  useReloadCustomizableCloset,
  useValidateCurrentCloset,
} from "@hooks/use-customizable-closet";
import { useCloseDrawer } from "@hooks/use-drawer";
import { useCurrentUser } from "@hooks/use-current-user";
import { getExpressDeliverySummary } from "@utils/call-api/closets-call";
import Button from "@components/core/Button";
import OffcanvasHeader from "@components/core/Offcanvas/OffcanvasHeader";
import ValidationSubheader from "./Elements/ValidationSubheader";
import ClosetItemsValidation from "./Elements/ClosetItemsValidation";
import ShippingAddressValidation from "./Elements/ShippingAddressValidation";
import ReturnModeValidation from "./Elements/ReturnModeValidation";
import ValidationSummary from "./Elements/ValidationSummary";

function initialExpressDelivery(expressDeliverySummary) {
  return Boolean(expressDeliverySummary?.eligible && expressDeliverySummary.selected);
}

function DrawerClosetRTCValidationStep({ setStep, open }) {
  const t = useTranslations("common");

  const { max_add_on_items: maxAddOnItems } = useFeatures();
  const { id: closetId, ready_to_pack_status: validationStatus, plan } = useCustomizableCloset();
  const lastClosetVersion = useCurrentUser()?.last_closet_version;
  const validateCurrentCloset = useValidateCurrentCloset();
  const reloadCurrentCloset = useReloadCustomizableCloset();
  const closeDrawer = useCloseDrawer();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expressDeliverySummary, setExpressDeliverySummary] = useState(null);
  const [expressDeliverySelected, setExpressDeliverySelected] = useState(false);

  useEffect(() => {
    async function fetchExpressDeliverySummary() {
      try {
        const summary = await getExpressDeliverySummary(closetId);
        setExpressDeliverySummary(summary);
        setExpressDeliverySelected(initialExpressDelivery(summary));
      } catch {
        setExpressDeliverySummary({ eligible: false });
        setExpressDeliverySelected(false);
      }
    }

    // Also refetches on lastClosetVersion changes: a validation error or an external closet
    // reload (see MonitorClosetVersion) can bring in a new closet without open/closetId changing,
    // and eligibility/price must not be left stale in that case.
    if (open) fetchExpressDeliverySummary();
  }, [open, closetId, lastClosetVersion]);

  function planContent() {
    return t("drawer.modal_rtc.plans.swapable_families", {
      count: plan.clothes + plan.accessories,
    });
  }

  const errorText = () => {
    if (validationStatus.status && !error) return null;

    const errorReason = validationStatus.reason || error;

    switch (errorReason) {
      case "no_address":
        return t("drawer.modal_rtc.errors.no_address");
      case "too_many_selected_items":
        return t("drawer.modal_rtc.errors.too_many_selected_items", {
          nbOfArticles: plan.accessories + plan.clothes + maxAddOnItems,
        });
      case "too_few_selected_items":
        return t("drawer.modal_rtc.errors.too_few_selected_items", { planContent: planContent() });
      default:
        return t("drawer.modal_rtc.errors.validate_closet_error");
    }
  };

  async function validateCloset() {
    if (isLoading) return;

    try {
      setIsLoading(true);
      await validateCurrentCloset(
        expressDeliverySummary?.eligible ? { expressDelivery: expressDeliverySelected } : {},
      );
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data?.code);
      reloadCurrentCloset();
    }
  }

  return (
    <div className={classNames("drawer-additional-panel", open && "open")}>
      <OffcanvasHeader
        closeButton
        onHide={closeDrawer}
        title={t("drawer.header.check_and_validate")}
      />

      <div className="drawer-body">
        <ValidationSubheader />

        <div className="d-flex flex-column p-2 gap-2">
          <ClosetItemsValidation setStep={setStep} />

          <ShippingAddressValidation
            closetId={closetId}
            open={open}
            expressDeliverySummary={expressDeliverySummary}
            expressDeliverySelected={expressDeliverySelected}
            onExpressDeliveryChange={setExpressDeliverySelected}
          />
          <ReturnModeValidation />
          <ValidationSummary
            expressDeliverySummary={expressDeliverySummary}
            expressDeliverySelected={expressDeliverySelected}
          />
        </div>
      </div>

      <div className="p-3 border-top border-light">
        {error && <p className="text-danger font-size-sm mb-3 text-center">{errorText()}</p>}

        <Button
          loading={isLoading}
          onClick={validateCloset}
          fullWidth
        >
          {t("drawer.modal_rtc.validate.validate_btn")}
        </Button>
      </div>
    </div>
  );
}

export default DrawerClosetRTCValidationStep;
